import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { ShieldAlert, User, CheckCircle, Trash2 } from 'lucide-react';
import usePolling from '@/hooks/usePolling';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GRAVEDAD_META = {
  leve: { label: 'Leve', dot: 'bg-green-500', badge: 'bg-green-100 text-green-700 border-green-200' },
  moderado: { label: 'Moderado', dot: 'bg-amber-500', badge: 'bg-amber-100 text-amber-700 border-amber-200' },
  grave: { label: 'Grave', dot: 'bg-red-500', badge: 'bg-red-100 text-red-700 border-red-200' },
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('es-MX', {
      day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const PrefecturaReportsSection = ({ defaultOpen = false, canAttend = false, showStatus = false, canDelete = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [attendingId, setAttendingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchReports = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await axios.get(`${API}/prefectura/reports`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setReports(res.data.reports || []);
    } catch (e) {
      if (!silent) setReports([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  // Real-time auto-refresh so new/attended reports appear without manual reload.
  const pollReports = useCallback(() => fetchReports(true), [fetchReports]);
  usePolling(pollReports, { intervalMs: 0 });

  const handleAttend = async (id) => {
    setAttendingId(id);
    try {
      await axios.put(`${API}/prefectura/reports/${id}/atender`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      toast.success('Reporte marcado como atendido');
      fetchReports();
    } catch (e) {
      toast.error('Error al marcar como atendido');
    } finally {
      setAttendingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este reporte de prefectura? Se quitará de todos los roles (profesor, orientador y padre) y del Historial de Conducta. Esta acción no se puede deshacer.')) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API}/prefectura/reports/${id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      toast.success('Reporte eliminado');
      fetchReports();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar el reporte');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Card className="p-4 mb-6" data-testid="prefectura-reports-section">
      <div className="flex items-center justify-between mb-3 cursor-pointer" onClick={() => setOpen(!open)}>
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-red-500" />
          Reportes de Prefectura
          {reports.length > 0 && (
            <span className="ml-2 bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full" data-testid="prefectura-reports-count">
              {reports.length}
            </span>
          )}
        </h2>
        <Button variant="ghost" size="sm" data-testid="prefectura-reports-toggle">
          {open ? 'Ocultar' : 'Mostrar'}
        </Button>
      </div>

      {open && (
        <div className="space-y-3" data-testid="prefectura-reports-list">
          {loading ? (
            <p className="text-sm text-slate-500 text-center py-4">Cargando...</p>
          ) : reports.length === 0 ? (
            <div className="text-center py-6 text-slate-500">
              <ShieldAlert className="w-10 h-10 mx-auto mb-2 opacity-40" />
              <p className="text-sm">No hay reportes de prefectura</p>
            </div>
          ) : (
            reports.map((r) => {
              const g = GRAVEDAD_META[(r.gravedad || 'leve')] || GRAVEDAD_META.leve;
              return (
                <div key={r.id} className="border border-red-200 bg-red-50 rounded-lg p-4" data-testid={`prefectura-report-item-${r.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-slate-500" />
                      <div>
                        <p className="font-semibold text-slate-800">{r.student_name}</p>
                        <p className="text-xs text-slate-500">
                          {r.matricula ? `Matrícula: ${r.matricula}` : 'Sin matrícula'}
                          {r.grupo ? ` · ${r.grupo}` : ''}{r.turno ? ` · ${r.turno}` : ''}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 text-xs font-bold rounded-full border ${g.badge}`} data-testid={`prefectura-gravedad-${r.id}`}>
                        <span className={`w-2.5 h-2.5 rounded-full ${g.dot}`}></span>
                        {g.label}
                      </span>
                      <span className="text-xs text-slate-500 whitespace-nowrap">{fmtDate(r.fecha || r.created_at)}</span>
                    </div>
                  </div>
                  <div className="mt-2 text-sm text-slate-700">
                    <p><strong>Motivo:</strong> {r.motivo}</p>
                    {r.descripcion && <p className="mt-1"><strong>Descripción:</strong> {r.descripcion}</p>}
                    <p className="text-xs text-slate-500 mt-1">Reportó: {r.prefecto_name || 'Prefecto'}</p>
                  </div>

                  {(showStatus || canAttend) && r.atendido && (
                    <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-md" data-testid={`prefectura-atendido-badge-${r.id}`}>
                      <CheckCircle className="w-3.5 h-3.5" />
                      Atendido{r.atendido_by ? ` por ${r.atendido_by}` : ''}{r.atendido_at ? ` · ${fmtDate(r.atendido_at)}` : ''}
                    </div>
                  )}

                  {canAttend && !r.atendido && (
                    <div className="mt-3">
                      <Button
                        size="sm"
                        onClick={() => handleAttend(r.id)}
                        disabled={attendingId === r.id}
                        className="bg-green-600 hover:bg-green-700 text-white"
                        data-testid={`prefectura-atender-${r.id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        {attendingId === r.id ? 'Marcando...' : 'Marcar como atendido'}
                      </Button>
                    </div>
                  )}

                  {canDelete && (
                    <div className="mt-3">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDelete(r.id)}
                        disabled={deletingId === r.id}
                        className="border-red-300 text-red-600 hover:bg-red-100"
                        data-testid={`prefectura-delete-${r.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        {deletingId === r.id ? 'Eliminando...' : 'Eliminar'}
                      </Button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      )}
    </Card>
  );
};

export default PrefecturaReportsSection;
